import { type ReactNode, useMemo, useState } from 'react';

import {
  PlayerSettingsContext,
  defaultPlayerSettings,
  mergePlayerSettings,
} from '../hooks/playerSettingsContext';
import { PlayerCameraControlsContext } from '../hooks/playerCameraControlsContext';
import { usePlayerHotkeys } from '../hooks/usePlayerHotkeys';
import {
  PlayerCharacterMeshProvider,
  type PlayerCharacterMeshOverrides,
} from '../hooks/playerCharacterMeshContext';
import { PlayerStoreProvider } from '../store/playerStore';
import type {
  DeepPartial,
  PlayerCameraControlsHandle,
  PlayerCameraInputMode,
  PlayerSettings,
} from '../types';

export interface PlayerRootProps {
  id?: string;
  children: ReactNode;
  initialSettings?: DeepPartial<PlayerSettings>;
  settings?: PlayerSettings;
  onSettingsChange?: (next: PlayerSettings) => void;
  hotkeysEnabled?: boolean;
  /** Optional FBX loader overrides (e.g. Cartographer parser for applets). */
  characterMesh?: PlayerCharacterMeshOverrides;
}

function PlayerRootInner({
  id = 'default',
  children,
  initialSettings,
  settings: controlledSettings,
  onSettingsChange,
  hotkeysEnabled = true,
  characterMesh,
}: PlayerRootProps) {
  const [cameraControls, setCameraControls] =
    useState<PlayerCameraControlsHandle | null>(null);
  const [inputMode, setInputMode] = useState<PlayerCameraInputMode>('rotate');
  const [uncontrolledSettings, setUncontrolledSettings] =
    useState<PlayerSettings>(() =>
      mergePlayerSettings(defaultPlayerSettings, initialSettings),
    );

  const settings = controlledSettings ?? uncontrolledSettings;

  usePlayerHotkeys({
    scope: `${id}-player`,
    enabled: hotkeysEnabled,
    cameraInputMode: inputMode,
    setCameraInputMode: setInputMode,
    cameraControls,
  });

  const contextValue = useMemo(
    () => ({
      settings,
      setSettings: (next: DeepPartial<PlayerSettings>) => {
        const merged = mergePlayerSettings(settings, next);
        if (!controlledSettings) {
          setUncontrolledSettings(merged);
        }
        onSettingsChange?.(merged);
      },
    }),
    [controlledSettings, onSettingsChange, settings],
  );
  const cameraControlsContextValue = useMemo(
    () => ({
      cameraControls,
      setCameraControls,
      inputMode,
      setInputMode,
    }),
    [cameraControls, inputMode],
  );

  return (
    <PlayerSettingsContext.Provider value={contextValue}>
      <PlayerCameraControlsContext.Provider value={cameraControlsContextValue}>
        <PlayerCharacterMeshProvider value={characterMesh}>
          {children}
        </PlayerCharacterMeshProvider>
      </PlayerCameraControlsContext.Provider>
    </PlayerSettingsContext.Provider>
  );
}

export default function PlayerRoot(props: PlayerRootProps) {
  if (props.children === undefined || props.children === null) {
    return null;
  }

  return (
    <PlayerStoreProvider>
      <PlayerRootInner {...props} />
    </PlayerStoreProvider>
  );
}
